import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { cn } from "helpers/classNames";
import { Requirement, RequirementKey } from "./loadingRequirements";

type Props = {
  requirements: Requirement[];
  onAction: (key: RequirementKey) => void;
  title: string;
  doneText: string;
};

export const OperatorChecklist = ({
  requirements,
  onAction,
  title,
  doneText,
}: Props) => {
  const fulfilledCount = requirements.filter((r) => r.isFulfilled).length;
  const allFulfilled = fulfilledCount === requirements.length;

  if (allFulfilled) {
    return (
      <div className="flex items-center gap-x-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
        <CheckCircleIcon
          className="h-7 w-7 shrink-0 text-green-600"
          aria-hidden="true"
        />
        <div>
          <p className="font-semibold text-green-800">Allt är ifyllt</p>
          <p className="text-sm text-green-700">{doneText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50/40">
      <div className="px-4 pb-3 pt-4">
        <div className="flex items-center justify-between gap-x-3">
          <h4 className="text-sm font-semibold text-blue-900">{title}</h4>
          <span className="shrink-0 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
            {fulfilledCount} av {requirements.length} klart
          </span>
        </div>
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-blue-100">
          <div
            className="h-full rounded-full bg-blue-600 transition-all duration-500"
            style={{
              width: `${(fulfilledCount / requirements.length) * 100}%`,
            }}
          />
        </div>
      </div>
      <ul className="divide-y divide-blue-100">
        {requirements.map((requirement) => (
          <li key={requirement.key}>
            <button
              type="button"
              onClick={() => onAction(requirement.key)}
              className="flex min-h-[3rem] w-full items-center gap-x-3 px-4 py-2.5 text-left transition-colors active:bg-blue-100/70"
            >
              {requirement.isFulfilled ? (
                <CheckCircleIcon
                  className="h-6 w-6 shrink-0 text-green-600"
                  aria-hidden="true"
                />
              ) : (
                <span
                  className="h-6 w-6 shrink-0 rounded-full border-2 border-dashed border-blue-400 bg-white"
                  aria-hidden="true"
                />
              )}
              <span
                className={cn(
                  "flex-1 text-sm",
                  requirement.isFulfilled
                    ? "text-gray-400"
                    : "font-semibold text-gray-900",
                )}
              >
                {requirement.label}
              </span>
              {!requirement.isFulfilled && (
                <span className="flex shrink-0 items-center gap-x-0.5 text-sm font-semibold text-blue-700">
                  Fyll i
                  <ChevronRightIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                    strokeWidth={2.5}
                  />
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
